/**
 * Elevation — the EIS Design System 2025 shadows (T-31, amending T-8).
 *
 * Cards float on an extremely soft, offset-free shadow instead of a border; dropdowns and menus
 * carry a short drop; the app bar is a hairline, not a shadow. Three named heights and a ramp that
 * collapses MUI's remaining steps onto the overlay — twelve subtly different shadows on one screen
 * is noise nobody can name.
 *
 * **Why a CSS variable for the shadow colour.** MUI's `shadows` array is one ramp for the whole
 * theme — `colorSchemes.dark` cannot override it — but a light ink is invisible on a dark navy
 * page and a black one is a smear on white. So the ramp is written once against
 * `--eis-shadow-ink`, which `MuiCssBaseline` redefines per colour scheme. Scheme switching stays
 * a single attribute flip with no React re-render, which is the reason CSS variables are on.
 */

/** RGB channels only — the alpha is supplied per step by `rgb(... / a)`. The design's `#111`. */
export const shadowInk = { light: '17 17 17', dark: '0 0 0' } as const;

const ink = (alpha: number): string => `rgb(var(--eis-shadow-ink) / ${String(alpha)})`;

/**
 * The named heights, so a component asks for "the card shadow" instead of picking an index that
 * later drifts from what every other card uses.
 */
export const elevation = {
  /** Cards, the header card, the table card, KPI tiles, the pill-tab strip. */
  card: `0 0 20px 0 ${ink(0.05)}`,
  /** The app bar: a hairline in the divider colour, not a shadow. */
  chrome: '0 1px 0 0 var(--mui-palette-divider)',
  /** Menus, popovers, dialogs, dropdowns. */
  overlay: `0 5px 15px 0 ${ink(0.1)}`,
  /** Tooltips. */
  tooltip: `0 0 10px 0 ${ink(0.1)}`,
} as const;

/**
 * MUI's `Shadows` is a 25-tuple; the array is built here and cast at the single use site in
 * `theme.ts` rather than spelling out twenty-five identical strings.
 */
export const shadows: readonly string[] = [
  'none',
  elevation.tooltip,
  elevation.card,
  elevation.card,
  elevation.overlay,
  ...Array.from({ length: 20 }, () => elevation.overlay),
];
